import React, { Component, useContext, useEffect } from 'react';
import { SvgIcon } from '../../icons';
import { combineClassNames } from '../../css';
import { useTranslate } from '../../translate';

export const Accordion = ({ children, isUpdating }) => (
  <div className={`accordion${isUpdating ? ' accordion--updating' : ''}`}>
    {children}
  </div>
);

export const AccordionGroupContext = React.createContext({
  isOpen: false,
  onToggle: () => {},
  displayOnly: false,
});

export class AccordionGroup extends Component {
  static defaultProps = {
    onToggle: () => {},
    className: 'accordion-item',
  };

  constructor(props) {
    super(props);
    this.state = {
      isOpen: props.isOpenDefault !== false,
      onToggle: this.onToggle,
      displayOnly: props.displayOnly,
    };
  }

  onToggle = () => {
    this.setState(
      (prevState) => ({
        isOpen: !prevState.isOpen,
      }),
      () => {
        this.props.onToggle();
      }
    );
  };

  render() {
    const { children, id, className } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        className={combineClassNames([
          'accordion-item',
          isOpen ? `${className}--active` : '',
          className !== 'accordion-item' ? className : '',
        ])}
        aria-expanded={isOpen}
        id={id}>
        <AccordionGroupContext.Provider value={this.state}>
          {children}
        </AccordionGroupContext.Provider>
      </div>
    );
  }
}

export const AccordionHeaderSecondary = ({ children, center }) => {
  return (
    <div
      className="accordion-title accordion-title--secondary"
      style={center ? { textAlign: 'center' } : null}>
      <div className="accordion-title__text">{children}</div>
    </div>
  );
};

const AccordionHeaderButton = ({
  isOpen,
  collapseIcon,
  expandIcon,
  className,
  ariaLabel = '',
}) => {
  return (
    <button aria-expanded={isOpen} className={className} aria-label={ariaLabel}>
      <SvgIcon
        name={collapseIcon || 'accordion-header-collapse'}
        className={`accordion-title__icon--collapse ${isOpen ? '' : 'hidden'}`}
      />
      <SvgIcon
        name={expandIcon || 'accordion-header-expand'}
        className={`accordion-title__icon--expand ${isOpen ? 'hidden' : ''}`}
      />
    </button>
  );
};

export const AccordionHeader = ({
  children,
  analyticsKey,
  expandIcon,
  collapseIcon,
  className,
  alignButtonLeft,
  headerButtonClass,
  useToggle = true,
  ariaLabel,
  open,
  onOpen,
  onIsOpenChanged,
}) => {
  const { isOpen, onToggle, displayOnly } = useContext(AccordionGroupContext);
  const headerClassName = className || 'accordion-item';
  const translate = useTranslate();
  const defaultAriaLabel = ariaLabel
    ? ariaLabel
    : translate('default.accordion.header.icon.label.v3');

  useEffect(() => {
    if (open) {
      if (!displayOnly && !isOpen) {
        onToggle();
      }
      onOpen && onOpen();
    }
  }, [open]);

  useEffect(() => {
    if (onIsOpenChanged) {
      onIsOpenChanged(isOpen);
    }
  }, [isOpen]);

  if (displayOnly) {
    return (
      <div
        data-dtm={'right rail'}
        className={combineClassNames([
          'accordion-title',
          'accordion-title--display-only',
          'stat-expand-icon',
          className,
          isOpen ? `${headerClassName}--is-active` : '',
        ])}>
        <div className="accordion-title__text">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={combineClassNames([
        'accordion-title',
        'stat-expand-icon',
        className,
        isOpen
          ? `${
              headerClassName === 'accordion-item'
                ? 'accordion-title'
                : headerClassName
            }--active`
          : '',
      ])}
      data-dtm={analyticsKey}
      onClick={useToggle ? onToggle : undefined}>
      {alignButtonLeft ? (
        <AccordionHeaderButton
          className={combineClassNames([
            'accordion-header-button',
            headerButtonClass,
          ])}
          isOpen={isOpen}
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          ariaLabel={defaultAriaLabel}
        />
      ) : null}
      <div className="accordion-title__text">{children}</div>
      {alignButtonLeft ? null : (
        <AccordionHeaderButton
          className={combineClassNames([
            'accordion-header-button--right',
            headerButtonClass,
          ])}
          isOpen={isOpen}
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          ariaLabel={defaultAriaLabel}
        />
      )}
    </div>
  );
};

export const AccordionContent = ({ children, className }) => {
  const { isOpen } = useContext(AccordionGroupContext);
  if (isOpen) {
    return (
      <div
        className={combineClassNames(['accordion-content', className])}
        style={{ display: 'block' }}>
        {children}
      </div>
    );
  }
  return null;
};

export const CustomAccordionHeader = ({ children, className }) => {
  return <div className={className}>{children}</div>;
};
